import { useState } from 'react'
import { supabase } from './supabase'

export default function CreateMatch({ session, editingMatch, onBack, onMatchCreated }) {
  const [matchName, setMatchName] = useState(editingMatch?.name || '')
  const [teeTime, setTeeTime] = useState(editingMatch?.tee_time ? editingMatch.tee_time.slice(0,16) : '')
  const [matchType, setMatchType] = useState(editingMatch?.match_type || 'private')
  const [courseName, setCourseName] = useState(editingMatch?.course_name || '')
  const [numHoles, setNumHoles] = useState(editingMatch?.num_holes || 18)
  const [holePars, setHolePars] = useState(editingMatch?.hole_pars || Array(18).fill(4))
  const [courseHcp, setCourseHcp] = useState(editingMatch?.course_handicap_index || '')
  const [courseRating, setCourseRating] = useState(editingMatch?.course_rating || '')
  const [slopeRating, setSlopeRating] = useState(editingMatch?.slope_rating || '')
  const [strokeIndex, setStrokeIndex] = useState(editingMatch?.stroke_index || Array(18).fill(''))
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const isEditing = !!editingMatch
  const totalPar = holePars.reduce((a, b) => a + b, 0)

  const handleHolesChange = (n) => {
    setNumHoles(n)
    setHolePars(prev => n > prev.length
      ? [...prev, ...Array(n - prev.length).fill(4)]
      : prev.slice(0, n))
    setStrokeIndex(prev => n > prev.length
      ? [...prev, ...Array(n - prev.length).fill('')]
      : prev.slice(0, n))
  }


  const handleSave = async () => {
    setError(null)
    if (!courseName.trim()) return setError('Please enter a course name')
    setLoading(true)
    const payload = {
      creator_id: session.user.id,
      name: matchName.trim() || null,
      tee_time: teeTime || null,
      match_type: matchType,
      state: 'open',
      course_name: courseName.trim(),
      num_holes: numHoles,
      hole_pars: holePars,
      course_par: totalPar,
      course_handicap_index: courseHcp ? parseFloat(courseHcp) : null,
      course_rating: courseRating ? parseFloat(courseRating) : null,
      slope_rating: slopeRating ? parseFloat(slopeRating) : null,
      stroke_index: strokeIndex.some(v => v !== '') ? strokeIndex.map(v => v !== '' ? parseInt(v) : null) : null,
    }
    if (isEditing) {
      const { error: e } = await supabase.from('matches').update(payload).eq('id', editingMatch.id)
      setLoading(false)
      if (e) return setError(e.message)
      onMatchCreated(editingMatch.id)
    } else {
      const { data, error: e } = await supabase.from('matches').insert(payload).select().single()
      setLoading(false)
      if (e) return setError(e.message)
      onMatchCreated(data.id)
    }
  }

  return (
    <div style={s.screen}>
      <div style={s.body}>

        <div style={s.intro}>
          <div style={s.introTitle}>SET UP YOUR MATCH</div>
          <div style={s.introSub}>Course info is required — betting lines are set up in the lobby</div>
        </div>

        {/* Match type */}
        <div style={s.field}>
          <div style={s.label}>MATCH TYPE</div>
          <div style={s.toggleRow}>
            {[['private', '🔒 Private'], ['public', '🌐 Public']].map(([v, l]) => (
              <button key={v} onClick={() => setMatchType(v)}
                style={{ ...s.toggleBtn, ...(matchType === v ? s.toggleBtnActive : {}) }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Match name */}
        <div style={s.field}>
          <div style={s.label}>MATCH NAME <span style={s.optional}>(optional)</span></div>
          <input style={s.input} placeholder="e.g. Saturday round with the boys"
            value={matchName} onChange={e => setMatchName(e.target.value)} />
        </div>

        {/* Tee time */}
        <div style={s.field}>
          <div style={s.label}>TEE TIME <span style={s.optional}>(optional)</span></div>
          <input style={s.input} type="datetime-local" value={teeTime} onChange={e => setTeeTime(e.target.value)} />
        </div>

        {/* Divider */}
        <div style={s.divider}>
          <div style={s.dividerLine} />
          <div style={s.dividerLabel}>COURSE SETUP</div>
          <div style={s.dividerLine} />
        </div>

        {/* Course name */}
        <div style={s.field}>
          <div style={s.label}>COURSE NAME</div>
          <input style={s.input} placeholder="e.g. Pebble Beach Golf Links"
            value={courseName} onChange={e => setCourseName(e.target.value)} />
        </div>

        {/* Number of holes */}
        <div style={s.field}>
          <div style={s.label}>NUMBER OF HOLES</div>
          <div style={s.holesRow}>
            {[9, 18].map(n => (
              <button key={n} onClick={() => handleHolesChange(n)}
                style={{ ...s.holeBtn, ...(numHoles === n ? s.holeBtnActive : {}) }}>{n}</button>
            ))}
            <input type="number" min="1" max="99"
              style={{ ...s.input, width: 90, textAlign: 'center' }}
              placeholder="Custom"
              onChange={e => { const v = parseInt(e.target.value); if (v > 0) handleHolesChange(v) }}
            />
          </div>
        </div>

        {/* Par per hole */}
        <div style={s.field}>
          <div style={s.label}>PAR PER HOLE <span style={s.parTotal}>Course Par: {totalPar}</span></div>
          <div style={{ overflowX: 'auto', paddingBottom: 8 }}>
            <div style={s.parInputRow}>
              {holePars.map((par, i) => (
                <div key={i} style={s.parInputCell}
                  onClick={() => setHolePars(prev => {
                    const a = [...prev]
                    a[i] = a[i] === 3 ? 4 : a[i] === 4 ? 5 : 3
                    return a
                  })}>
                  <div style={s.parHoleNum}>{i + 1}</div>
                  <div style={{ ...s.parTap, background: par === 3 ? 'rgba(0,86,63,0.12)' : par === 5 ? 'rgba(193,154,107,0.2)' : '#FFFFFF' }}>{par}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Course handicap index */}
        <div style={s.field}>
          <div style={s.label}>COURSE HANDICAP INDEX <span style={s.optional}>(optional)</span></div>
          <input style={s.input} type="number" step="0.1"
            placeholder="e.g. 72.4" value={courseHcp}
            onChange={e => setCourseHcp(e.target.value)} />
        </div>

        {/* Course Rating and Slope */}
        <div style={s.field}>
          <div style={s.label}>COURSE RATING <span style={s.optional}>(optional)</span></div>
          <input style={s.input} type="number" step="0.1"
            placeholder="e.g. 71.2" value={courseRating}
            onChange={e => setCourseRating(e.target.value)} />
        </div>

        <div style={s.field}>
          <div style={s.label}>SLOPE RATING <span style={s.optional}>(optional)</span></div>
          <input style={s.input} type="number" step="1"
            placeholder="e.g. 125" value={slopeRating}
            onChange={e => setSlopeRating(e.target.value)} />
        </div>

        {/* Stroke Index per hole */}
        <div style={s.field}>
          <div style={s.label}>STROKE INDEX PER HOLE <span style={s.optional}>(optional — required for handicap)</span></div>
          <div style={s.fieldNote}>The difficulty ranking of each hole (1 = hardest, {numHoles} = easiest)</div>
          <div style={{ overflowX: 'auto', paddingBottom: 8, marginTop: 4 }}>
            <div style={s.parInputRow}>
              {strokeIndex.map((si, i) => (
                <div key={i} style={s.parInputCell}>
                  <div style={s.parHoleNum}>{i + 1}</div>
                  <input type="number" min="1" max={numHoles} value={si}
                    onChange={e => {
                      const newSI = [...strokeIndex]
                      newSI[i] = e.target.value
                      setStrokeIndex(newSI)
                    }}
                    style={s.parInput} placeholder="-" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {(!courseRating || !slopeRating) && (
          <div style={s.hcpWarning}>
            ⚠️ Without Course Rating and Slope Rating, this round won't count toward your CROSSBET Handicap calculation
          </div>
        )}

        {error && <div style={s.error}>{error}</div>}

        <button style={{ ...s.btn, opacity: loading ? 0.7 : 1 }} onClick={handleSave} disabled={loading}>
          {loading ? 'Saving...' : isEditing ? 'Save Changes →' : 'Continue to Invite Players →'}
        </button>

      </div>
    </div>
  )
}

const s = {
  screen: { display: 'flex', flexDirection: 'column', fontFamily: "'DM Sans', sans-serif", background: '#FDF9E7' },
  body: { padding: '20px 24px 48px', display: 'flex', flexDirection: 'column', gap: 18 },
  intro: { marginBottom: 4 },
  introTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: '#1A1814', letterSpacing: 1 },
  introSub: { fontSize: 13, color: '#7A7570', marginTop: 4 },
  field: { display: 'flex', flexDirection: 'column', gap: 8 },
  label: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 1.5, textTransform: 'uppercase' },
  optional: { fontSize: 11, fontWeight: 400, color: '#B0ACA4', letterSpacing: 0, textTransform: 'none' },
  parTotal: { fontSize: 11, fontWeight: 600, color: '#00563F', letterSpacing: 0, textTransform: 'none' },
  input: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 16px', fontSize: 15, color: '#1A1814', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  toggleRow: { display: 'flex', gap: 10 },
  toggleBtn: { flex: 1, background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 12, padding: '13px 8px', fontSize: 14, color: '#7A7570', cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 },
  toggleBtnActive: { background: 'rgba(0,96,57,0.08)', border: '1.5px solid #00563F', color: '#00563F' },
  divider: { display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' },
  dividerLine: { flex: 1, height: 1, background: '#E0DDD4' },
  dividerLabel: { fontSize: 11, fontWeight: 700, color: '#7A7570', letterSpacing: 2 },
  holesRow: { display: 'flex', gap: 8, alignItems: 'center' },
  holeBtn: { background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 10, padding: '10px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', color: '#1A1814' },
  holeBtnActive: { background: 'rgba(0,96,57,0.08)', border: '1.5px solid #00563F', color: '#00563F' },
  parInputRow: { display: 'flex', gap: 6, minWidth: 'max-content' },
  parInputCell: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  parHoleNum: { fontSize: 9, color: '#7A7570', fontWeight: 700 },
  parTap: { width: 36, height: 36, borderRadius: 8, border: '1.5px solid #D0CCC0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: '#1A1814', cursor: 'pointer', userSelect: 'none' },
  parInput: { width: 38, height: 38, textAlign: 'center', background: '#FFFFFF', border: '1.5px solid #D0CCC0', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#1A1814', fontFamily: 'inherit', outline: 'none' },
  hcpWarning: { background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 12, padding: '12px 16px', fontSize: 12, color: '#9B7D52', lineHeight: 1.6 },
  error: { background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.25)', borderRadius: 12, padding: '12px 16px', fontSize: 14, color: '#C0392B' },
  btn: { background: 'linear-gradient(135deg,#00563F,#006B4D)', color: 'white', border: 'none', borderRadius: 14, padding: '16px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.3 },
}
